import { GAMBIT_URL } from '../config/gambitEndpoint';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { set_user } from './appSlice';
import type { StudioState } from '../components/studio/studioModel';
import type { SavedChapter } from '../components/studio/studioSavedAdapter';
import type { StudioPanels, StudioTree, StudioProjectListSnapshot } from '../components/studio/useStudioPreferences';
import type { MessagePage } from '../components/studio/studioHistory';

export const STUDIO_CACHE_SCOPE = `v2:${window.location.origin}:${GAMBIT_URL}`;
export const STUDIO_CACHE_FRESH_MS = 60_000;
type Cached<T> = { data: T; updatedAt: number };
export type StudioLibraryData = { pages: { stories: any[]; lastEvaluatedKey?: any }[]; pageParams: any[] };
type Layout = { panels?: StudioPanels; tree?: StudioTree; list?: StudioProjectListSnapshot };
export type StudioCacheData = {
  library?: Cached<StudioLibraryData>;
  pins?: Cached<{ pins: string[] }>;
  chapters: Record<string, Cached<{ chapters: SavedChapter[] }>>;
  workspace?: Cached<StudioState>;
  layout: Layout;
};
export type StudioSession = { workspace: StudioState; pages: Record<string, MessagePage>; updatedAt: number };
export type StudioCacheState = {
  activeOwnerId: string | null;
  ownerId: string | null;
  scope: string;
  data: StudioCacheData;
  session?: StudioSession;
};
const emptyData = (): StudioCacheData => ({ chapters: {}, layout: {} });
const initialState: StudioCacheState = { activeOwnerId: null, ownerId: null, scope: '', data: emptyData() };
type Owner = { ownerId: string; scope?: string };
function accept(state: StudioCacheState, payload: Owner) {
  if (!payload.ownerId || payload.ownerId !== state.activeOwnerId) return false;
  const scope = payload.scope || STUDIO_CACHE_SCOPE;
  if (state.ownerId !== payload.ownerId || state.scope !== scope) {
    state.ownerId = payload.ownerId; state.scope = scope; state.data = emptyData(); delete state.session;
  }
  return true;
}
const copy = <T,>(value: T): T => JSON.parse(JSON.stringify(value));
/** Persist sidebar metadata only. Prompts, transcripts, media URLs, balances,
 * credentials and access grants are deliberately absent from this projection. */
export function sidebarWorkspace(snapshot: StudioState): StudioState {
  return { schema: 1, projectId: snapshot.projectId, chapterId: snapshot.chapterId, balance: 0,
    projects: snapshot.projects.slice(0, 250).map(project => ({
      id: project.id, title: project.title, autoTitle: project.autoTitle, starter: project.starter,
      pinned: project.pinned, updatedAt: project.updatedAt, lastMessageCursor: project.lastMessageCursor,
      continuation: project.continuation, continuationSource: project.continuationSource,
      chapters: project.chapters.slice(0, 100).map(chapter => ({
        id: chapter.id, title: chapter.title, chapterNumber: chapter.chapterNumber,
        activeSequenceId: chapter.activeSequenceId, activeConversationId: chapter.activeConversationId,
        jobs: chapter.jobs?.map(job => ({ id: job.id, status: job.status, stage: job.stage, progress: "", })),
        conversations: chapter.conversations.map(conversation => ({ id: conversation.id, title: conversation.title, messages: [] })),
        sequences: chapter.sequences.map(sequence => ({ id: sequence.id, name: sequence.name, note: '', locked: sequence.locked,
          publishedStory: sequence.publishedStory, localStory: sequence.localStory,
          artifacts: Object.fromEntries(Object.entries(sequence.artifacts).map(([stage, artifact]) => [stage,
            artifact && { id: artifact.id, title: artifact.title, stage: artifact.stage, text: '', detail: '', inputIds: [] }])) })),
      })),
    })) };
}
export function sidebarChapters(chapters: SavedChapter[]): SavedChapter[] {
  return chapters.slice(0, 100).map(({ storyId, title, chapterNumber, mode, pending, incomplete, pendingVideoCreation, pendingFFmpeg }) =>
    ({ storyId, title, chapterNumber, mode, pending, incomplete, pendingVideoCreation, pendingFFmpeg }));
}
export function sidebarLibrary(data: StudioLibraryData): StudioLibraryData {
  const fields = ['storyId', 'storyName', 'chapterTitle', 'rootStoryTitle', 'storyTitle', 'chapterIds',
    'createDate', 'updateDate', 'updatedAt', 'createdAt', 'realtimeWatchUntil', 'realtimeOperationId',
    'pending', 'failed', 'pendingChapterCount'];
  return { pages: data.pages.slice(0, 20).map(page => ({ lastEvaluatedKey: page.lastEvaluatedKey,
    stories: page.stories.map(row => Object.fromEntries(fields.filter(key => row[key] !== undefined).map(key => [key, row[key]]))) })),
    pageParams: data.pageParams.slice(0, 20) };
}
const slice = createSlice({ name: 'studioCache', initialState, reducers: {
  cacheStudioLibrary(state, action: PayloadAction<Owner & Cached<StudioLibraryData>>) {
    if (!accept(state, action.payload) || (state.data.library?.updatedAt || 0) > action.payload.updatedAt) return;
    // Retain a contiguous prefix so its last cursor can safely fetch more.
    state.data.library = { data: copy(sidebarLibrary(action.payload.data)), updatedAt: action.payload.updatedAt };
  },
  cacheStudioPins(state, action: PayloadAction<Owner & Cached<{ pins: string[] }>>) {
    if (!accept(state, action.payload) || (state.data.pins?.updatedAt || 0) > action.payload.updatedAt) return;
    state.data.pins = { data: { pins: action.payload.data.pins.slice(0, 100) }, updatedAt: action.payload.updatedAt };
  },
  cacheStudioChapters(state, action: PayloadAction<Owner & Cached<{ chapters: SavedChapter[] }> & { storyId: string }>) {
    if (!accept(state, action.payload)) return;
    const { storyId, data, updatedAt } = action.payload;
    if ((state.data.chapters[storyId]?.updatedAt || 0) > updatedAt) return;
    state.data.chapters[storyId] = { data: { chapters: sidebarChapters(data.chapters) }, updatedAt };
    const ids = Object.keys(state.data.chapters).sort((a, b) => state.data.chapters[b].updatedAt - state.data.chapters[a].updatedAt);
    for (const id of ids.slice(100)) delete state.data.chapters[id];
  },
  reconcileStudioDeletion(state, action: PayloadAction<Owner & { storyId: string; remaining?: { storyId: string | null; chapterIds: string[] } }>) {
    if (!accept(state, action.payload)) return;
    const { storyId, remaining } = action.payload;
    const library = state.data.library;
    const root = library?.data.pages.flatMap(page => page.stories).find(row => row.storyId === storyId);
    const affected = new Set([storyId, ...(root?.chapterIds || [])]);
    if (library) {
      library.data.pages.forEach(page => { page.stories = page.stories.flatMap(row => {
        if (row.storyId === storyId && remaining?.storyId) return [{ ...row, storyId: remaining.storyId, chapterIds: remaining.chapterIds }];
        return affected.has(row.storyId) ? [] : [row];
      }); });
      library.updatedAt = 0;
    }
    for (const id of affected) delete state.data.chapters[id];
    if (remaining?.storyId) delete state.data.chapters[remaining.storyId];
    if (state.data.pins) {
      state.data.pins.data.pins = [...new Set(state.data.pins.data.pins.flatMap(pin =>
        pin === `story:${storyId}` ? remaining?.storyId ? [`story:${remaining.storyId}`] : [] : [pin]))];
      state.data.pins.updatedAt = 0;
    }
    // Reconcile any draft links from the service before restoring a full chat.
    delete state.session;
  },
  forgetStudioChapters(state, action: PayloadAction<Owner & { storyId: string }>) {
    if (accept(state, action.payload)) delete state.data.chapters[action.payload.storyId];
  },
  cacheStudioWorkspace(state, action: PayloadAction<Owner & StudioSession>) {
    if (!accept(state, action.payload)) return;
    state.data.workspace = { data: copy(sidebarWorkspace(action.payload.workspace)), updatedAt: action.payload.updatedAt };
    // Full state stays in memory. Persistence keeps only a bounded, short-lived
    // view of the active chapter in this tab's sessionStorage.
    state.session = copy({ workspace: action.payload.workspace, pages: Object.fromEntries(Object.entries(action.payload.pages).slice(-40)), updatedAt: action.payload.updatedAt });
  },
  revealStudioChapter(state, action: PayloadAction<Owner & { workspace: StudioState }>) {
    if (!accept(state, action.payload)) return;
    const { projectId, chapterId, projects } = action.payload.workspace;
    const project = projects.find(item => item.id === projectId);
    if (!project?.chapters.some(chapter => chapter.id === chapterId)) return;
    const tree = state.data.layout.tree ||= { expandedProjects: [], expandedChapters: [], collapsedSections: [] };
    const root = project.continuation?.storyId || project.continuationSource?.storyId;
    tree.expandedProjects = [...new Set([...tree.expandedProjects, projectId, ...(root ? [`saved-${root}`] : [])])];
    tree.expandedChapters = [...new Set([...tree.expandedChapters, chapterId])];
    const pins = state.data.pins?.data.pins || [];
    const section = project.pinned || pins.includes(`draft:${projectId}`) || root && pins.includes(`story:${root}`) ? 'pinned' : 'stories';
    tree.collapsedSections = tree.collapsedSections.filter(item => item !== section);
  },
  cacheStudioLayout(state, action: PayloadAction<Owner & Layout>) {
    if (!accept(state, action.payload)) return;
    for (const key of ['panels', 'tree', 'list'] as const) {
      const value = action.payload[key];
      if (value !== undefined) (state.data.layout as any)[key] = copy(value);
    }
  },
}, extraReducers: builder => {
  builder.addCase(set_user, (state, action) => {
    const owner = action.payload.userId;
    if (!owner || state.ownerId && state.ownerId !== owner) {
      state.ownerId = null; state.scope = ''; state.data = emptyData(); delete state.session;
    }
    state.activeOwnerId = owner;
  });
} });
export function selectStudioCache(state: { app: { userId: string | null }; studioCache?: StudioCacheState }) {
  const cache = state.studioCache;
  return state.app.userId && cache?.ownerId === state.app.userId && cache.scope === STUDIO_CACHE_SCOPE ? cache : undefined;
}
export const { cacheStudioLibrary, cacheStudioPins, cacheStudioChapters, forgetStudioChapters, reconcileStudioDeletion, cacheStudioWorkspace, cacheStudioLayout, revealStudioChapter } = slice.actions;
export default slice.reducer;
